import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Skeleton,
  Tabs,
  Tab,
} from "@mui/material";
import { DateRange, Download } from "@mui/icons-material";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import api from "../../utils/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ExportTab from "./ViolationsExportPage";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const GlassCard = ({ children, sx, ...props }) => (
  <Card
    elevation={0}
    sx={{
      ...adminCardSx,
      p: { xs: 2, md: 3 },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Card>
);

const StatTile = ({ label, value, sublabel }) => (
  <GlassCard
    sx={{
      textAlign: "center",
      py: 3,
      height: "100%",
    }}
  >
    <Typography
      variant="overline"
      sx={{
        letterSpacing: 1,
        color: "#6b778c",
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>

    <Typography
      variant="h3"
      sx={{
        fontWeight: 800,
        lineHeight: 1.1,
        color: "#223142",
        my: 0.75,
      }}
    >
      {value}
    </Typography>

    {sublabel && (
      <Typography variant="caption" sx={{ color: "#6b778c" }}>
        {sublabel}
      </Typography>
    )}
  </GlassCard>
);

const SectionHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" sx={{ color: "#6b778c" }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function offsetDays(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function firstOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function addDaysISO(dateStr, days) {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const presets = [
  { key: "30d", label: "Last 30 days", get: () => ({ start: offsetDays(30), end: todayISO() }) },
  { key: "90d", label: "Last 90 days", get: () => ({ start: offsetDays(90), end: todayISO() }) },
  { key: "mtd", label: "MTD", get: () => ({ start: firstOfMonthISO(), end: todayISO() }) },
  { key: "ytd", label: "YTD", get: () => ({ start: `${new Date().getFullYear()}-01-01`, end: todayISO() }) },
];

const softColors = [
  "#A3E4D7",
  "#F9E79F",
  "#AED6F1",
  "#F5B7B1",
  "#D2B4DE",
  "#A9CCE3",
  "#FAD7A0",
  "#A2D9CE",
  "#F5CBA7",
  "#BB8FCE",
];

function makeTopNPieData(counts, n = 8, label = "Other") {
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const top = entries.slice(0, n);
  const rest = entries.slice(n);
  const otherTotal = rest.reduce((sum, [, c]) => sum + c, 0);

  if (otherTotal > 0) top.push([label, otherTotal]);

  return {
    labels: top.map(([k]) => k),
    datasets: [
      {
        data: top.map(([, v]) => v),
        backgroundColor: top.map((_, i) => softColors[i % softColors.length]),
        borderWidth: 2,
        borderColor: "#fff",
        hoverBorderColor: "#fff",
      },
    ],
  };
}

const ViolationsTab = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [violations, setViolations] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [includeMember, setIncludeMember] = useState(true);
  const [membershipType, setMembershipType] = useState("");
  const [membershipTypeOptions, setMembershipTypeOptions] = useState([]);
  const [violationType, setViolationType] = useState("");
  const [violationTypeOptions, setViolationTypeOptions] = useState([]);
  const overviewRef = useRef(null);
  const optionsLoadedRef = useRef(false);

  useEffect(() => {
    const { start, end } = presets.find((p) => p.key === "90d").get();
    setStartDate(start);
    setEndDate(end);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchViolations(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, propertyId, includeMember, membershipType, violationType]);

  const fetchViolations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", addDaysISO(endDate, 1));
      if (propertyId.trim()) params.append("property_id", propertyId.trim());
      if (includeMember) params.append("includeMember", "1");
      if (membershipType.trim()) {
        params.append("club_membership_type_code", membershipType.trim());
      }
      if (violationType.trim()) {
        params.append("violation_type_title", violationType.trim());
      }

      const { data } = await api.get(`/getViolationsReport?${params.toString()}`);
      const rows = data || [];
      setViolations(rows);

      // Accumulate options. never shrink them when a filter is active
      const newMemberships = [
        ...new Set(rows.map((v) => v?.member?.ClubMembershipTypeCode).filter(Boolean)),
      ];
      setMembershipTypeOptions((prev) => {
        const existing = new Set(prev.map((o) => o.value));
        const added = newMemberships.filter((c) => !existing.has(c)).map((c) => ({ value: c, label: c }));
        return added.length ? [...prev, ...added] : prev;
      });

      const newTypes = [...new Set(rows.map((v) => v?.violation_type_title).filter(Boolean))];
      setViolationTypeOptions((prev) => {
        const existing = new Set(prev.map((o) => o.value));
        const added = newTypes.filter((t) => !existing.has(t)).map((t) => ({ value: t, label: t }));
        return added.length ? [...prev, ...added] : prev;
      });
    } catch (e) {
      console.error("Error fetching violations:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPropertyId("");
    setMembershipType("");
    setViolationType("");
    setIncludeMember(true);
    const { start, end } = presets.find((p) => p.key === "90d").get();
    setStartDate(start);
    setEndDate(end);
  };

  const total = violations.length;
  const closedStatuses = ["closed", "violation cured", "withdrawn / canceled"];

  const closed = violations.filter((v) =>
    closedStatuses.includes((v.status || "").toLowerCase())
  ).length;

  const open = total - closed;

  const thisMonthViolations = useMemo(() => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();

    return violations.filter((v) => {
      const d = v.created_at ? new Date(v.created_at) : null;
      return d && d.getMonth() === month && d.getFullYear() === year;
    }).length;
  }, [violations]);

  const statusCounts = violations.reduce((acc, v) => {
    const st = v.status || "Unknown";
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  const sortedStatuses = Object.entries(statusCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  const typeCounts = violations.reduce((acc, v) => {
    const type = v.violation_type_title || "Unknown Type";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const sortedTypes = Object.entries(typeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  const membershipCounts = violations.reduce((acc, v) => {
    const code = v?.member?.ClubMembershipTypeCode || "Unknown";
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  const lettersByAction = violations.reduce((acc, v) => {
    if (!v.letter_id) return acc;
    const act = v.letter_action || "Unknown";
    acc[act] = (acc[act] || 0) + 1;
    return acc;
  }, {});

  const lettersByAddress = violations.reduce((acc, v) => {
    if (!v.letter_id) return acc;
    const addr = v.letter_address_type || "Unknown";
    acc[addr] = (acc[addr] || 0) + 1;
    return acc;
  }, {});

  const monthlyCounts = useMemo(() => {
    const counts = {};
    violations.forEach((v) => {
      const d = v.created_at ? new Date(v.created_at) : null;
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    const sortedKeys = Object.keys(counts).sort();
    const last12 = sortedKeys.slice(-12);

    return {
      labels: last12,
      data: last12.map((k) => counts[k]),
    };
  }, [violations]);

  const monthlyComparison = useMemo(() => {
    if (!violations.length) return { labels: [], current: [], previous: [] };

    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = lastMonthDate.getMonth();
    const lastYear = lastMonthDate.getFullYear();

    const daysThisMonth = new Date(thisYear, thisMonth + 1, 0).getDate();
    const daysLastMonth = new Date(lastYear, lastMonth + 1, 0).getDate();

    const labels = Array.from(
      { length: Math.max(daysThisMonth, daysLastMonth) },
      (_, i) => i + 1
    );
    const current = Array(labels.length).fill(0);
    const previous = Array(labels.length).fill(0);

    violations.forEach((v) => {
      const d = v.created_at ? new Date(v.created_at) : null;
      if (!d) return;

      if (d.getFullYear() === thisYear && d.getMonth() === thisMonth) {
        current[d.getDate() - 1] += 1;
      } else if (d.getFullYear() === lastYear && d.getMonth() === lastMonth) {
        previous[d.getDate() - 1] += 1;
      }
    });

    return { labels, current, previous };
  }, [violations]);

  const statusChartData = {
    labels: sortedStatuses.map(([s]) => s),
    datasets: [
      {
        label: "Violations",
        data: sortedStatuses.map(([, c]) => c),
        backgroundColor: "rgba(47,99,173,0.68)",
        borderRadius: 8,
        barThickness: 18,
      },
    ],
  };

  const typeChartData = {
    labels: sortedTypes.map(([t]) => t),
    datasets: [
      {
        label: "Violations",
        data: sortedTypes.map(([, c]) => c),
        backgroundColor: "rgba(220,38,38,0.58)",
        borderRadius: 8,
        barThickness: 18,
      },
    ],
  };

  const membershipChartData = {
    labels: Object.keys(membershipCounts),
    datasets: [
      {
        label: "Violations",
        data: Object.values(membershipCounts),
        backgroundColor: "rgba(34,197,94,0.58)",
        borderRadius: 8,
        barThickness: 18,
      },
    ],
  };

  const lettersByActionChartData = {
    labels: Object.keys(lettersByAction),
    datasets: [
      {
        label: "Letters",
        data: Object.values(lettersByAction),
        backgroundColor: "rgba(249,115,22,0.58)",
        borderRadius: 8,
        barThickness: 18,
      },
    ],
  };

  const lettersByAddressChartData = {
    labels: Object.keys(lettersByAddress),
    datasets: [
      {
        label: "Letters",
        data: Object.values(lettersByAddress),
        backgroundColor: "rgba(59,130,246,0.58)",
        borderRadius: 8,
        barThickness: 18,
      },
    ],
  };

  const monthlyChartData = {
    labels: monthlyCounts.labels,
    datasets: [
      {
        label: "Violations Created",
        data: monthlyCounts.data,
        fill: true,
        backgroundColor: "rgba(47,99,173,0.16)",
        borderColor: "rgba(47,99,173,0.85)",
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  };

  const comparisonChartData = {
    labels: monthlyComparison.labels,
    datasets: [
      {
        label: "This Month",
        data: monthlyComparison.current,
        borderColor: "rgba(47,99,173,0.85)",
        backgroundColor: "rgba(47,99,173,0.16)",
        fill: true,
        tension: 0.3,
        pointRadius: 3,
      },
      {
        label: "Last Month",
        data: monthlyComparison.previous,
        borderColor: "rgba(220,38,38,0.8)",
        backgroundColor: "rgba(220,38,38,0.12)",
        fill: true,
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  };

  const statusPieData = makeTopNPieData(statusCounts, 8, "Other");
  const typePieData = makeTopNPieData(typeCounts, 8, "Other");

  const barOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#223142",
        backgroundColor: "rgba(255,255,255,0.92)",
        borderColor: "rgba(0,0,0,0.12)",
        borderWidth: 1,
        borderRadius: 4,
        font: {
          weight: "bold",
          size: 11,
        },
        padding: 4,
        anchor: "end",
        align: "right",
        clip: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: "rgba(47,99,173,0.08)" },
      },
      y: {
        grid: { display: false },
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      datalabels: { display: false },
    },
    scales: {
      x: { grid: { color: "rgba(47,99,173,0.06)" } },
      y: { beginAtZero: true, grid: { color: "rgba(47,99,173,0.08)" } },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "55%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            weight: "600",
          },
        },
      },
      datalabels: {
        formatter: (value, ctx) => {
          const total =
            ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0) || 1;
          const pct = Math.round((value / total) * 100);
          return `${pct}%`;
        },
        color: "#223142",
        backgroundColor: "rgba(255,255,255,0.92)",
        borderColor: "rgba(0,0,0,0.12)",
        borderWidth: 1,
        borderRadius: 6,
        padding: 6,
        font: {
          weight: "bold",
          size: 11,
        },
        anchor: "center",
        align: "center",
        clip: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0) || 1;
            const val = ctx.raw;
            const pct = Math.round((val / total) * 100);
            return `${ctx.label}: ${val} (${pct}%)`;
          },
        },
      },
    },
  };

  const handleExportOverviewToPDF = async () => {
    if (!overviewRef.current) return;

    const canvas = await html2canvas(overviewRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    if (imgHeight <= pageHeight) {
      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
    } else {
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
      }
    }

    pdf.save(`Violations_Overview_${todayISO()}.pdf`);
  };

  return (
    <Box sx={{ width: "100%" }}>
 

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 2.5,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  mb: 0.5,
                }}
              >
                Violations Overview
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Switch between dashboard overview and export tools.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExportOverviewToPDF}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Download PDF
            </Button>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Violations Tabs"
            sx={{
              minHeight: "auto",
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                minHeight: 42,
                color: "#5f6b7a",
                mr: 1,
                px: 2,
              },
              "& .Mui-selected": {
                color: "#2f63ad !important",
                backgroundColor: "rgba(47,99,173,0.08)",
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab label="Overview" />
            <Tab label="Export" />
          </Tabs>
        </Box>
      </Card>

      {activeTab === 0 && (
        <Box ref={overviewRef}>
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Total" value={total} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Closed" value={closed} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="Open" value={open} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatTile label="This Month's Violations" value={thisMonthViolations} />
            </Grid>
          </Grid>

          <GlassCard sx={{ mb: 2.5 }}>
            <SectionHeader
              title="Filters"
              subtitle="Filter violation reporting by property, member type, type, and date range."
            />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Member / Property ID"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Membership Type</InputLabel>
                  <Select
                    value={membershipType}
                    label="Membership Type"
                    onChange={(e) => setMembershipType(e.target.value)}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {membershipTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Violation Type</InputLabel>
                  <Select
                    value={violationType}
                    label="Violation Type"
                    onChange={(e) => setViolationType(e.target.value)}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {violationTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  fullWidth
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  fullWidth
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeMember}
                        onChange={(e) => setIncludeMember(e.target.checked)}
                      />
                    }
                    label="Include member info"
                  />
                </Box>
              </Grid>
            </Grid>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              sx={{ mt: 2.5, gap: 1 }}
            >
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {presets.map((p) => {
                  const { start, end } = p.get();
                  const active = startDate === start && endDate === end;

                  return (
                    <Chip
                      key={p.key}
                      icon={<DateRange />}
                      size="small"
                      label={p.label}
                      color={active ? "primary" : "default"}
                      variant={active ? "filled" : "outlined"}
                      onClick={() => {
                        setStartDate(start);
                        setEndDate(end);
                      }}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 600,
                      }}
                    />
                  );
                })}
              </Stack>

              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={handleReset}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2,
                }}
              >
                Reset Filters
              </Button>
            </Stack>
          </GlassCard>

          <GlassCard sx={{ mb: 2.5 }}>
            <SectionHeader
              title="Trend: This Month vs Last Month"
              subtitle="Daily comparison of newly created violations."
            />
            <Box sx={{ height: 320 }}>
              {loading ? (
                <Skeleton height={320} />
              ) : (
                <Line data={comparisonChartData} options={lineOptions} />
              )}
            </Box>
          </GlassCard>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Top 10 Statuses"
                  subtitle="Highest volume statuses in the selected range."
                />
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton height={320} />
                  ) : (
                    <Bar data={statusChartData} options={barOptions} />
                  )}
                </Box>
              </GlassCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Top 10 Violation Types"
                  subtitle="Most frequent violation types in the selected range."
                />
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton height={320} />
                  ) : (
                    <Bar data={typeChartData} options={barOptions} />
                  )}
                </Box>
              </GlassCard>
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Status Breakdown"
                  subtitle="Top status distribution with smaller categories grouped."
                />
                <Box sx={{ height: 360, maxWidth: 520, mx: "auto" }}>
                  {loading ? (
                    <Skeleton height={360} />
                  ) : statusPieData.labels.length ? (
                    <Doughnut data={statusPieData} options={pieOptions} />
                  ) : (
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      No data.
                    </Typography>
                  )}
                </Box>
              </GlassCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Violation Type Breakdown"
                  subtitle="Top type distribution with smaller categories grouped."
                />
                <Box sx={{ height: 360, maxWidth: 520, mx: "auto" }}>
                  {loading ? (
                    <Skeleton height={360} />
                  ) : typePieData.labels.length ? (
                    <Doughnut data={typePieData} options={pieOptions} />
                  ) : (
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      No data.
                    </Typography>
                  )}
                </Box>
              </GlassCard>
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Violations by Membership Type"
                  subtitle="Grouped by club membership type code."
                />
                {!includeMember && (
                  <Typography variant="body2" sx={{ color: "#6b778c", mb: 1.5 }}>
                    Enable &quot;Include member info&quot; to see this chart.
                  </Typography>
                )}
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton height={320} />
                  ) : membershipChartData.labels.length ? (
                    <Bar data={membershipChartData} options={barOptions} />
                  ) : (
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      No membership data available.
                    </Typography>
                  )}
                </Box>
              </GlassCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Violations by Created Month"
                  subtitle="Monthly creation trend across the latest 12 months."
                />
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton height={320} />
                  ) : (
                    <Line data={monthlyChartData} options={lineOptions} />
                  )}
                </Box>
              </GlassCard>
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Letters Logged by Action"
                  subtitle="Logged letter counts grouped by letter action."
                />
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton height={320} />
                  ) : (
                    <Bar data={lettersByActionChartData} options={barOptions} />
                  )}
                </Box>
              </GlassCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <GlassCard>
                <SectionHeader
                  title="Letters Logged by Address Type"
                  subtitle="Logged letter counts grouped by address type."
                />
                <Box sx={{ height: 320 }}>
                  {loading ? (
                    <Skeleton height={320} />
                  ) : (
                    <Bar data={lettersByAddressChartData} options={barOptions} />
                  )}
                </Box>
              </GlassCard>
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 1 && <ExportTab />}
    </Box>
  );
};

export default ViolationsTab;
